import { ApolloClient, gql } from 'apollo-boost';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  ProgramAnswer,
  ProgramEnumResponse,
  ProgramQuestionValidation,
} from 'shared/providers/ProgramProvider/types';

/**
 * Snapshot question shape returned by the dedicated
 * `snapshotReportQuestions(reportId)` resolver. The backend already filters
 * by the report's reporting_type and excludes program-level / INTRO
 * questions, so the client receives exactly the set of rows to render.
 * Fields are aligned with the fragments ProgramProvider consumers use so
 * we can reshape into a ProgramQuestionsResult without fabrication.
 */
export interface RawSnapshotQuestion {
  dbId: number;
  text: string | null;
  name: string | null;
  section: FeedbackSectionId | null;
  sectionStart: boolean | null;
  questionType: string | null;
  required: boolean | null;
  helpContent: string | null;
  reportingType: string | null;
  nextQuestionId: number | null;
  isProgramQuestion?: boolean;
  enumResponses: ProgramEnumResponse[];
  validations: ProgramQuestionValidation[];
  answers: ProgramAnswer[];
}

const enumResponseFragment = `
  dbId
  order
  responseType
  response
  responseValue
  responseHelp
  questionId
  nextQuestionId
  treeGroup
`;

const validationFragment = `
  dbId
  validation
  validationType
  text
`;

const answerFragment = `
  id
  dbId
  questionId
  programId
  text
  date1
  date2
  answerId
  answerTime
  answerUserId
  questionText
  autoAnswered
  resourceId
  enumResponse { ${enumResponseFragment} }
`;

const snapshotQuestionsQuery = gql`
  query snapshotReportQuestions($reportId: Int!) {
    snapshotReportQuestions(reportId: $reportId) {
      dbId
      text
      name
      section
      sectionStart
      questionType
      required
      helpContent
      reportingType
      nextQuestionId
      isProgramQuestion
      enumResponses { edges { node { ${enumResponseFragment} } } }
      validations { edges { node { ${validationFragment} } } }
      answers(filters: { reportId: $reportId }) {
        edges { node { ${answerFragment} } }
      }
    }
  }
`;

// removeConnections is a shared helper; import from the same barrel as the
// rest of the app. Kept internal to this module since only the shapes we
// unwrap here matter.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = <T>(conn: any): T[] =>
  (conn?.edges ?? []).map((e: { node: T }) => e.node);

export const fetchSnapshotQuestions = async (
  client: ApolloClient<object>,
  reportId: number,
): Promise<RawSnapshotQuestion[]> => {
  const res = await client.query({
    query: snapshotQuestionsQuery,
    variables: { reportId },
    fetchPolicy: 'network-only',
  });
  // The `snapshotReportQuestions` field returns a plain list, but the
  // nested enumResponses/validations/answers are still relay connections
  // that need edge/node unwrapping.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = (res.data as any)?.snapshotReportQuestions ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return rows.map((q: any) => ({
    ...q,
    enumResponses: unwrap<ProgramEnumResponse>(q.enumResponses),
    validations: unwrap<ProgramQuestionValidation>(q.validations),
    answers: unwrap<ProgramAnswer>(q.answers),
  }));
};
